/**
 * React Hook for Credit Checking
 * 
 * Use this hook to check if user can perform premium actions
 * Works for both anonymous and authenticated users
 * NEVER breaks the app - fails open if credit check fails
 * 
 * Global refresh: dispatch 'garu:credits-updated' event to refresh all instances
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { checkCredits, CreditStatus } from '@/services/creditService';
import { useAdminMode } from '@/contexts/AdminModeContext';

/** Dispatch this event to trigger all useCredits hooks to refresh */
export const CREDITS_UPDATED_EVENT = 'garu:credits-updated';

/** Helper to notify all useCredits instances to refresh */
export function notifyCreditsUpdated() {
  window.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT));
}

export function useCredits() {
  const { user } = useAuth();
  const { isAdminMode } = useAdminMode();
  const [creditStatus, setCreditStatus] = useState<CreditStatus>({
    tier: 'anonymous',
    hasCredits: true, // Default to true to avoid blocking during load
    triesRemaining: 3,
    triesUsed: 0,
    needsUpgrade: false,
    upgradeMessage: ''
  });
  const [isChecking, setIsChecking] = useState(true);

  const refreshCredits = useCallback(async () => {
    setIsChecking(true);
    try {
      const status = await checkCredits(user, isAdminMode);
      setCreditStatus(status);
    } catch (error) {
      console.error('[useCredits] Error:', error);
      setCreditStatus({
        tier: user ? 'free' : 'anonymous',
        hasCredits: true,
        triesRemaining: 1,
        triesUsed: 0,
        needsUpgrade: false,
        upgradeMessage: ''
      });
    } finally {
      setIsChecking(false);
    }
  }, [user, isAdminMode]);

  // Initial fetch + re-fetch when user/adminMode changes
  useEffect(() => {
    let mounted = true;

    const checkUserCredits = async () => {
      setIsChecking(true);
      
      try {
        const status = await checkCredits(user, isAdminMode);
        
        if (mounted) {
          setCreditStatus(status);
        }
      } catch (error) {
        console.error('[useCredits] Error:', error);
        
        // Fail open: If there's an error, allow the action
        if (mounted) {
          setCreditStatus({
            tier: user ? 'free' : 'anonymous',
            hasCredits: true,
            triesRemaining: 1,
            triesUsed: 0,
            needsUpgrade: false,
            upgradeMessage: ''
          });
        }
      } finally {
        if (mounted) {
          setIsChecking(false);
        }
      }
    };

    checkUserCredits();

    return () => {
      mounted = false;
    };
  }, [user, isAdminMode]);

  // Listen for global credit update events (fired after try-on success)
  useEffect(() => {
    const handleCreditsUpdated = () => {
      refreshCredits();
    };

    window.addEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
    return () => {
      window.removeEventListener(CREDITS_UPDATED_EVENT, handleCreditsUpdated);
    };
  }, [refreshCredits]);

  return {
    ...creditStatus,
    isChecking,
    refreshCredits,
    // Convenience flags
    isAnonymous: creditStatus.tier === 'anonymous',
    isFree: creditStatus.tier === 'free',
    isPro: creditStatus.tier === 'pro',
    isAdmin: creditStatus.tier === 'admin',
    hasUnlimited: creditStatus.triesRemaining === 'unlimited'
  };
}
